import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../common/ConfirmDialog';
import { Mic, Play, Download, Trash2, X, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

function fmtDur(s) { const m = Math.floor(s/60); return m + 'm ' + (s%60) + 's'; }

export default function RecordingsPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', src: '', dst: '' });

  const params = new URLSearchParams({ page, limit: 50, ...Object.fromEntries(Object.entries(filters).filter(([,v]) => v)) });
  const { data, isLoading, refetch, isFetching } = useQuery(
    ['recordings', page, filters],
    () => api.get('/recordings?' + params).then(r => r.data),
    { keepPreviousData: true, refetchInterval: 20000, refetchOnWindowFocus: true }
  );

  const delMut = useMutation((id) => api.delete('/recordings/' + id),
    { onSuccess: () => { qc.invalidateQueries('recordings'); toast.success('Grabacion eliminada'); },
      onError: () => toast.error('Error') });

  const [playing, setPlaying] = useState(null); // { url, label }

  // Reproducir: descarga el audio CON el token (axios) y lo reproduce desde un blob
  const playRec = async (rec) => {
    try {
      const res = await api.get('/recordings/' + rec.id + '/play', { responseType: 'blob' });
      if (playing?.url) URL.revokeObjectURL(playing.url);
      setPlaying({ url: URL.createObjectURL(res.data), label: `${rec.src} → ${rec.dst}` });
    } catch { toast.error('No se pudo reproducir la grabación'); }
  };

  const downloadRec = async (rec) => {
    try {
      const res = await api.get('/recordings/' + rec.id + '/download', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = rec.filename || ('grabacion_' + rec.id + '.wav');
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error('No se pudo descargar la grabación'); }
  };

  const closePlayer = () => {
    if (playing?.url) URL.revokeObjectURL(playing.url);
    setPlaying(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Grabaciones</h1>
          <p className="text-gray-400 text-sm">{data?.total || 0} grabaciones · se actualiza cada 20s</p>
        </div>
        <button onClick={() => refetch()} disabled={isFetching}
          className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm transition-colors disabled:opacity-50">
          <RefreshCw size={15} className={isFetching ? 'animate-spin' : ''} /> Actualizar
        </button>
      </div>

      <div className="card">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <input aria-label="Origen" value={filters.src} onChange={e => setFilters(f => ({ ...f, src: e.target.value }))} className="input" placeholder="Origen..." />
          <input aria-label="Destino" value={filters.dst} onChange={e => setFilters(f => ({ ...f, dst: e.target.value }))} className="input" placeholder="Destino..." />
          <input aria-label="Fecha inicio" type="date" value={filters.startDate} onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))} className="input" />
          <input aria-label="Fecha fin" type="date" value={filters.endDate} onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))} className="input" />
        </div>

        {isLoading ? <div className="flex justify-center py-12"><div className="spinner" /></div> : (
          <table className="w-full">
            <thead><tr>
              {['Fecha', 'Origen', 'Destino', 'Duracion', 'Tamano', 'Acciones'].map(h => <th key={h} className="table-th">{h}</th>)}
            </tr></thead>
            <tbody>
              {(data?.data || []).map(rec => (
                <tr key={rec.id} className="table-row">
                  <td className="table-td text-xs">{format(new Date(rec.created_at), 'dd/MM/yy HH:mm')}</td>
                  <td className="table-td font-mono text-blue-400">{rec.src}</td>
                  <td className="table-td font-mono">{rec.dst}</td>
                  <td className="table-td">{fmtDur(rec.duration || 0)}</td>
                  <td className="table-td text-xs">{rec.file_size ? (rec.file_size / 1024).toFixed(0) + ' KB' : '-'}</td>
                  <td className="table-td">
                    <div className="flex gap-1">
                      <button onClick={() => playRec(rec)} aria-label="Reproducir" className="p-1.5 text-gray-400 hover:text-green-400 rounded-lg"><Play size={15} /></button>
                      <button onClick={() => downloadRec(rec)} aria-label="Descargar" className="p-1.5 text-gray-400 hover:text-blue-400 rounded-lg"><Download size={15} /></button>
                      <button onClick={async () => { if (await confirm({ title: 'Eliminar la grabación', message: 'Esta acción no se puede deshacer.', confirmLabel: 'Eliminar', danger: true })) delMut.mutate(rec.id); }}
                        aria-label="Eliminar" className="p-1.5 text-gray-400 hover:text-red-400 rounded-lg"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!data?.data?.length && <tr><td colSpan={6} className="text-center py-12 text-muted">Sin grabaciones</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {/* Reproductor flotante */}
      {playing && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-3 flex items-center gap-3 w-[min(90vw,480px)]">
          <div className="text-xs text-gray-400 font-mono whitespace-nowrap">{playing.label}</div>
          <audio src={playing.url} controls autoPlay className="flex-1 h-9" />
          <button onClick={closePlayer} aria-label="Cerrar reproductor" className="p-1.5 text-gray-400 hover:text-red-400 rounded-lg"><X size={16} /></button>
        </div>
      )}
    </div>
  );
}
